import {
  Component,
  computed,
  Signal,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { FirebaseService } from '../../../_services/firebase.service';
import { FirestoreStoreService } from '../../../_services/firestore-store.service';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { JsEmail } from '../../../_interfaces/email.interface';
import { DateFnsModule } from 'ngx-date-fns';
import { EmailFormComponent } from '../email-form/email-form.component';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { EmailTemplatePreviewComponent } from '../email-template-preview/email-template-preview.component';
import { EmailHashUtil } from '../../../_utils/hash.util';
import { sortLanguageCodes } from '../../../_utils/utils';
import { getLanguageName } from '../../../_datas/languages.data';
import { StorageService } from '../../../_services/storage.service';

@Component({
  selector: 'app-email',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    MatTooltip,
    DateFnsModule,
    RouterLink,
  ],
  templateUrl: './email.component.html',
  styleUrl: './email.component.scss',
})
export class EmailComponent {
  public fbs = inject(FirebaseService);
  public dialog = inject(MatDialog);
  private store = inject(FirestoreStoreService);
  private storageService = inject(StorageService);

  emailSearch: FormControl = new FormControl('');

  // Use store for email data - automatically updates in real-time!
  allEmailDatas: Signal<JsEmail[]> = this.store.activeEmails;

  searchQuery: WritableSignal<string> = signal('');

  emailDatas: Signal<JsEmail[]> = computed(() => {
    const query = this.searchQuery();
    return this.allEmailDatas().filter((email) => {
      const matchesQuery =
        query === '' ||
        email.templateName.toLowerCase().includes(query.toLowerCase());

      return matchesQuery && !email.deletedAt;
    });
  });

  /** Check if emails are loading */
  isLoading(): boolean {
    return this.store.emailsLoading();
  }

  /** Clear search input */
  clearSearch(): void {
    this.emailSearch.setValue('');
    this.searchQuery.set('');
  }

  openEmailForm(mode: 'new' | 'edit', email?: JsEmail) {
    const confirmDialog = this.dialog.open(EmailFormComponent, {
      width: '500px !important',
      maxWidth: '500px !important',
      maxHeight: '80vh',
      disableClose: true,
      data: {
        mode: mode,
        value: email,
      },
    });
    return confirmDialog.afterClosed();
  }

  previewEmail(email: JsEmail, event: Event) {
    event.stopPropagation();

    // Use the default language for preview display
    const previewLangCode = email.defaultLangCode || 'en';
    const htmlContent = email.htmlPart?.[previewLangCode] || '';
    const subject = email.subjectPart?.[previewLangCode] || 'No Subject';

    // Build ALL language options for test email dialog (sorted: English first, then alphabetical)
    const allLanguageCodes = sortLanguageCodes(Object.keys(email.htmlPart || {}));
    const languages = allLanguageCodes.map(langCode => ({
      languageCode: langCode,
      languageName: getLanguageName(langCode),
      isValid: !!email.htmlPart?.[langCode]?.trim(),
      hasContent: !!email.htmlPart?.[langCode]?.trim()
    }));

    this.dialog.open(EmailTemplatePreviewComponent, {
      width: '100%',
      maxWidth: '900px',
      height: '100%',
      maxHeight: '90vh',
      panelClass: 'email-preview-dialog-panel',
      data: {
        templateName: email.templateName,
        htmlPart: { [previewLangCode]: htmlContent },
        subjectPart: { [previewLangCode]: subject },
        templateVariables: email.templateVariables,
        defaultLangCode: previewLangCode,
        languageCodes: [previewLangCode],
        languages,
        defaultEmail: this.storageService.getUser()?.email || '',
        // Use raw HTML content from local template (not AWS SES template)
        source: 'editor' as const,
      },
    });
  }

  trackByForGroup(index: number, group: string) {
    return group;
  }

  trackByForEmailList(index: number, email: JsEmail) {
    return email.id;
  }

  trackByForCol(index: number, col: string) {
    return col;
  }

  /**
   * Get publication status of an email template
   * @returns 'never-published' | 'published' | 'unpublished'
   */
  getPublicationStatus(email: JsEmail): string {
    if (!email.publishedSnapshot) {
      return 'never-published';
    }
    const hasChanges = EmailHashUtil.hasUnpublishedChanges(
      email.contentHash,
      email.publishedSnapshot
    );
    return hasChanges ? 'unpublished' : 'published';
  }

  /**
   * Get publication status badge class
   */
  getPublicationBadgeClass(email: JsEmail): string {
    const status = this.getPublicationStatus(email);
    return status === 'published' ? 'badge-published' :
           status === 'unpublished' ? 'badge-unpublished' :
           'badge-never-published';
  }

  /**
   * Get publication status label
   */
  getPublicationLabel(email: JsEmail): string {
    const status = this.getPublicationStatus(email);
    return status === 'published' ? 'Published' :
           status === 'unpublished' ? 'Unpublished Changes' :
           'Never Published';
  }

  /**
   * Get tooltip for published time showing status and who published
   */
  getPublicationTooltip(email: JsEmail): string {
    if (!email.publishedSnapshot) {
      return 'Never published';
    }
    const status = this.getPublicationStatus(email);
    const publishedBy = this.fbs.getUserNameFromId(email.publishedSnapshot.publishedBy) || 'Unknown';
    if (status === 'unpublished') {
      return `Has unpublished changes · Published by ${publishedBy}`;
    }
    return `Published by ${publishedBy}`;
  }
}
